// 永続化コーディネータ — debounce 自動保存と離脱時 flush を提供。
//
// 使用例:
//   const persister = createPersister(() => snapshot());
//   persister.markDirty();   // 状態変更後に呼ぶ
//   await persister.flush(); // 強制保存

import type { AppState } from './types';
import { saveState } from './db';

const DEBOUNCE_MS = 500;

export interface Persister {
  /** 状態変更を通知し、debounce 後に自動保存する。 */
  markDirty(): void;
  /** 待機中の保存を即座に実行する。離脱直前等で利用。 */
  flush(): Promise<void>;
  /** debounce タイマー解除（テスト用）。 */
  dispose(): void;
}

export function createPersister(snapshot: () => AppState): Persister {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;
  let inFlight: Promise<void> | null = null;

  async function doSave(): Promise<void> {
    pending = false;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    const state = snapshot();
    inFlight = saveState(state).finally(() => {
      inFlight = null;
    });
    await inFlight;
  }

  function markDirty(): void {
    pending = true;
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      void doSave();
    }, DEBOUNCE_MS);
  }

  async function flush(): Promise<void> {
    if (inFlight) {
      await inFlight;
    }
    if (pending) {
      await doSave();
    }
  }

  function dispose(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  // ページ離脱時の取りこぼし防止
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      // pagehide 内では非同期 flush は完了保証されないが、
      // pending を即時同期 save にする努力はする。
      if (pending) {
        void doSave();
      }
    });
  }

  return { markDirty, flush, dispose };
}
